import { EnvironmentOutlined, PhoneOutlined } from '@ant-design/icons';
import { Button, Card, Divider, Form, Input, message, Modal, Space, Tag, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import TwZipCode from 'react-twzipcode';
import { useCart } from '../contexts/CartContext';
import { useI18n } from '../i18n';
import { apiService } from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface CheckoutModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  preSelectedCoupons?: number[];
  preSelectedDiscount?: number;
  selectedItems?: Set<number>;
}

const CheckoutModal: React.FC<CheckoutModalProps> = ({ visible, onClose, onSuccess, preSelectedCoupons = [], preSelectedDiscount = 0, selectedItems }) => {
  const { t } = useI18n();
  const { state, clearCart, removeItem } = useCart();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedCoupons, setSelectedCoupons] = useState<number[]>(preSelectedCoupons);
  const [totalDiscount, setTotalDiscount] = useState(preSelectedDiscount);
  const [savedShippingAddress, setSavedShippingAddress] = useState<string>('');
  const [savedContactPhone, setSavedContactPhone] = useState<string>('');
  const [zipCodeData, setZipCodeData] = useState({
    county: '',
    district: '',
    zipcode: ''
  });

  // 监听props变化，更新优惠券选择
  useEffect(() => {
    setSelectedCoupons(preSelectedCoupons);
    setTotalDiscount(preSelectedDiscount);
  }, [preSelectedCoupons, preSelectedDiscount]);

  // 获取用户保存的收货地址
  useEffect(() => {
    const fetchShippingAddress = async () => {
      try {
        const response = await apiService.getShippingAddress();
        if (response.success) {
          setSavedShippingAddress(response.shippingAddress || '');
          setSavedContactPhone(response.contactPhone || '');

          // 自动填充表单
          if (response.shippingAddress || response.contactPhone) {
            form.setFieldsValue({
              address: response.shippingAddress || '',
              contactPhone: response.contactPhone || ''
            });
          }
        }
      } catch (error) {
        console.error('获取收货地址失败:', error);
      }
    };

    if (visible) {
      fetchShippingAddress();
    }
  }, [visible, form]);

  // 处理取消订单
  const handleCancelOrder = () => {
    Modal.confirm({
      title: t('messages.checkout.confirmCancel'),
      content: t('messages.checkout.cancelConfirmation'),
      okText: t('messages.checkout.confirmCancel'),
      cancelText: t('messages.checkout.continueOrder'),
      okType: 'danger',
      onOk() {
        // 清空表单
        form.resetFields();
        // 重置步骤
        setCurrentStep(0);
        // 关闭模态框
        onClose();
        message.info(t('messages.checkout.orderCancelled'));
      },
    });
  };

  const normalizeCounty = (value: any) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      return value.county || value.name || value.label || '';
    }
    return '';
  };

  const normalizeDistrict = (value: any) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      return value.district || value.name || value.label || '';
    }
    return '';
  };

  const normalizeZipcode = (value: any) => {
    if (!value) return '';
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      return value.zipcode || value.code || '';
    }
    return '';
  };

  // 处理优惠券选择变化
  const handleCouponSelectionChange = (couponIds: number[], discount: number) => {
    setSelectedCoupons(couponIds);
    setTotalDiscount(discount);
  };

  // 验证优惠券
  const validateCoupons = () => {
    if (selectedCoupons.length === 0) return true;

    // 这里可以添加更详细的优惠券验证逻辑
    // 比如检查优惠券是否仍然可用、是否满足使用条件等
    return true;
  };

  // 处理订单提交
  const handleSubmitOrder = async (values: any) => {
    if (state.items.length === 0) {
      message.warning(t('messages.checkout.cartEmpty'));
      return;
    }

    // 如果没有传入selectedItems，使用所有商品；否则只使用选中的商品
    const itemsToProcess = selectedItems
      ? state.items.filter(item => selectedItems.has(item.product.id))
      : state.items;

    if (itemsToProcess.length === 0) {
      message.warning(t('cart.selectItemsWarning'));
      return;
    }

    // 验证优惠券
    if (!validateCoupons()) {
      message.error(t('messages.checkout.couponValidationFailed'));
      return;
    }

    setLoading(true);
    try {
      // 组合完整地址：邮编 + 县市 + 区 + 详细地址
      const fullAddress = `${zipCodeData.zipcode ? zipCodeData.zipcode + ' ' : ''}${zipCodeData.county}${zipCodeData.district}${values.address || ''}`;

      const orderData = {
        orderType: 'PURCHASE',
        orderItems: itemsToProcess.map(item => ({
          goodsId: item.product.id,
          quantity: item.quantity,
          notes: '',
        })),
        shippingAddress: fullAddress,
        billingAddress: fullAddress, // 使用相同的地址作为账单地址
        contactPhone: values.contactPhone, // 联系方式
        notes: values.notes,
        paymentMethod: 'COD', // 货到付款
        couponIds: selectedCoupons, // 选择的优惠券ID列表
        totalDiscount: totalDiscount, // 总折扣金额
      };

      const response = await apiService.createOrder(orderData);

      if (response.success) {
        message.success(response.message || t('messages.checkout.createdSuccessWithInfo'));

        // 保存收货地址到用户资料
        try {
          await apiService.updateShippingAddress(fullAddress, values.contactPhone);
        } catch (error) {
          console.error('保存收货地址失败:', error);
          // 不影响订单创建，只记录错误
        }

        // 只清除选中的商品，如果没有selectedItems则清空整个购物车
        if (selectedItems && selectedItems.size > 0) {
          // 逐个删除选中的商品
          Array.from(selectedItems).forEach(productId => {
            removeItem(productId);
          });
        } else {
          // 如果没有选择特定商品，清空整个购物车（兼容旧逻辑）
          clearCart();
        }

        // 清空表单和重置步骤
        form.resetFields();
        setCurrentStep(0);
        // 关闭模态框
        onClose();
        // 调用成功回调
        onSuccess?.();
      } else {
        message.error(response.message || t('messages.checkout.createdFailedRetry'));
      }
    } catch (error) {
      message.error(t('messages.checkout.createdFailed'));
      console.error('Error creating order:', error);
    } finally {
      setLoading(false);
    }
  };



  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            {/* 收货信息表单 */}
            <Card title={t('messages.checkout.shippingInfo')} style={{ marginBottom: '16px' }}>
              <Form form={form} layout="vertical">
                {/* 地址选择区域 */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    color: 'rgba(0, 0, 0, 0.85)',
                    fontWeight: 500
                  }}>
                    <EnvironmentOutlined style={{ marginRight: '6px', color: '#1890ff' }} />
                    <span>{t('messages.checkout.shippingAddress')}</span>
                    <span style={{ color: '#ff4d4f', marginLeft: '4px' }}>*</span>
                  </div>

                  <div style={{
                    background: '#f9f9f9',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #e8e8e8'
                  }}>
                    {/* 地址选择提示 */}
                    <div style={{ marginBottom: '12px' }}>
                      <Text type="secondary" style={{ fontSize: '13px' }}>
                        請選擇縣市和鄉鎮區
                      </Text>
                    </div>

                    {/* 台湾地址选择器 - 美化版 */}
                    <div style={{
                      marginBottom: '16px',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      <style>{`
                        .tw-zipcode-container select {
                          height: 36px !important;
                          padding: 4px 11px !important;
                          font-size: 14px !important;
                          line-height: 1.5715 !important;
                          border: 1px solid #d9d9d9 !important;
                          border-radius: 6px !important;
                          transition: all 0.3s !important;
                          min-width: 120px !important;
                        }
                        .tw-zipcode-container select:hover {
                          border-color: #40a9ff !important;
                        }
                        .tw-zipcode-container select:focus {
                          border-color: #40a9ff !important;
                          box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
                          outline: none !important;
                        }
                        .tw-zipcode-container input {
                          height: 36px !important;
                          padding: 4px 11px !important;
                          font-size: 14px !important;
                          border: 1px solid #d9d9d9 !important;
                          border-radius: 6px !important;
                          width: 100px !important;
                        }
                        .tw-zipcode-container input:focus {
                          border-color: #40a9ff !important;
                          box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
                          outline: none !important;
                        }
                      `}</style>
                      <div className="tw-zipcode-container">
                        <TwZipCode
                          css={[
                            {
                              display: 'inline-block',
                              marginRight: '8px'
                            },
                            {
                              display: 'inline-block',
                              marginRight: '8px'
                            },
                            {
                              display: 'inline-block'
                            }
                          ]}
                          handleChangeCounty={(county) => {
                            setZipCodeData(prev => ({ ...prev, county: normalizeCounty(county) }));
                          }}
                          handleChangeDistrict={(district) => {
                            setZipCodeData(prev => ({ ...prev, district: normalizeDistrict(district) }));
                          }}
                          handleChangeZipcode={(zipcode) => {
                            setZipCodeData(prev => ({ ...prev, zipcode: normalizeZipcode(zipcode) }));
                          }}
                        />
                      </div>
                    </div>

                    {/* 显示已选择的地址信息 */}
                    {(zipCodeData.county || zipCodeData.district || zipCodeData.zipcode) && (
                      <div style={{
                        marginBottom: '12px',
                        padding: '8px 12px',
                        background: '#e6f7ff',
                        borderRadius: '6px',
                        border: '1px solid #91d5ff',
                        fontSize: '13px'
                      }}>
                        <EnvironmentOutlined style={{ marginRight: '6px', color: '#1890ff' }} />
                        <span>已選擇：</span>
                        {zipCodeData.zipcode && <Tag color="blue" style={{ margin: '0 4px' }}>{zipCodeData.zipcode}</Tag>}
                        <span style={{ fontWeight: 600 }}>{zipCodeData.county}{zipCodeData.district}</span>
                      </div>
                    )}

                    {savedShippingAddress && (
                      <div style={{ marginBottom: '12px', textAlign: 'right' }}>
                        <Button
                          type="link"
                          size="small"
                          icon={<EnvironmentOutlined />}
                          onClick={() => {
                            form.setFieldsValue({
                              address: savedShippingAddress,
                              contactPhone: savedContactPhone
                            });
                          }}
                          style={{ padding: '4px 8px' }}
                        >
                          使用上次地址
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* 详细地址输入 */}
                <Form.Item
                  name="address"
                  label={
                    <Text type="secondary" style={{ fontSize: '13px' }}>
                      詳細地址
                    </Text>
                  }
                  rules={[{ required: true, message: t('messages.checkout.shippingAddressRequired') }]}
                >
                  <TextArea
                    rows={3}
                    placeholder="請輸入街道名稱、巷弄、門牌號碼等詳細地址"
                    style={{
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="contactPhone"
                  label={
                    <span>
                      <PhoneOutlined style={{ marginRight: '6px', color: '#52c41a' }} />
                      {t('messages.checkout.contactPhone')}
                    </span>
                  }
                  rules={[{ required: true, message: t('messages.checkout.contactPhoneRequired') }]}
                >
                  <Input
                    placeholder={t('messages.checkout.contactPhonePlaceholder')}
                    prefix={<PhoneOutlined style={{ color: '#bfbfbf' }} />}
                    style={{ borderRadius: '6px' }}
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="notes"
                  label={t('messages.checkout.notes')}
                >
                  <TextArea
                    rows={2}
                    placeholder={t('messages.checkout.notesPlaceholder')}
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Form>
            </Card>



            {/* 支付方式和配送说明 */}
            {/* 订单总价信息 */}
            <Card style={{ marginTop: '16px' }}>
              <div style={{ background: '#f0f2f5', padding: '16px', borderRadius: '6px' }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>{t('messages.checkout.subtotal')}</Text>
                    <Text>NT${(selectedItems ? state.items.filter(item => selectedItems.has(item.product.id)) : state.items).reduce((sum, item) => sum + ((item.product.price || 0) * item.quantity), 0).toFixed(2)}</Text>
                  </div>
                  {totalDiscount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text style={{ color: '#52c41a' }}>{t('messages.checkout.couponDiscount')}</Text>
                      <Text style={{ color: '#52c41a' }}>-NT${totalDiscount.toFixed(2)}</Text>
                    </div>
                  )}
                  <Divider style={{ margin: '8px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text strong style={{ fontSize: '16px' }}>{t('messages.checkout.total')}</Text>
                    <Text strong style={{ fontSize: '16px', color: '#ff4d4f' }}>
                      NT${Math.max(0, (selectedItems ? state.items.filter(item => selectedItems.has(item.product.id)) : state.items).reduce((sum, item) => sum + ((item.product.price || 0) * item.quantity), 0) - totalDiscount).toFixed(2)}
                    </Text>
                  </div>
                </Space>
              </div>
            </Card>

            <Card style={{ marginTop: '16px' }}>
              <div style={{ background: '#f6ffed', padding: '12px', borderRadius: '6px', border: '1px solid #b7eb8f' }}>
                <Text>
                  <strong>{t('messages.checkout.paymentMethod')}:</strong> {t('messages.checkout.cod')}<br />
                  <strong>{t('messages.checkout.deliveryNote')}:</strong> {t('messages.checkout.deliveryDescription')}
                </Text>
              </div>
            </Card>
          </div>
        );


      default:
        return null;
    }
  };

  return (
    <Modal
      title={t('messages.checkout.title')}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      style={{ top: 20 }}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >

      <div style={{ marginBottom: '24px' }}>
        {renderStepContent()}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ marginTop: '16px', width: '100%' }}>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button onClick={handleCancelOrder}>{t('messages.checkout.cancel')}</Button>
            <Button
              type="primary"
              size="large"
              loading={loading}
              onClick={() => form.submit()}
            >
              {t('messages.checkout.confirm')}
            </Button>
          </Space>
        </div>
      </div>

      <Form
        form={form}
        onFinish={handleSubmitOrder}
        style={{ display: 'none' }}
      />
    </Modal>
  );
};

export default CheckoutModal;
